// src/pages/expert/AcquisitionProposals.jsx
// Espace Art Advisor : propositions d'acquisition (sélections d'œuvres NON détenues),
// prix demandé + argumentaire par œuvre, lien de partage 14 jours, réactions du client.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { newProposalToken, REACTION_LABELS } from "../../lib/proposalApi";
import { useTranslation } from "react-i18next";
import { useFT } from "../../locales/foundationCopy";
import ImportOffersPdf from "../../components/expert/ImportOffersPdf";
import { galleryPriceHint, missingPrices } from "../../utils/offerImport";
import { downloadProposalClientPdf, imageToDataUrl } from "../../utils/proposalClientPdf";
import { clearProposalCollectionCache } from "../../lib/proposalExclusion";

const PRICE_COPY = {
  fr: {
    excl: "Prix HT", incl: "Prix TTC", currency: "Devise", why: "Pourquoi cette œuvre pour ce client ?", save: "Enregistrer",
    gallery: (a, cur) => `Prix de la galerie : ${Number(a).toLocaleString("fr-FR")} ${cur || ""}`.trim(),
    asHt: "Reprendre comme HT", asTtc: "Reprendre comme TTC", unspecified: "Le document ne précise pas HT ou TTC : à vous de choisir.",
    taxExcl: "(HT)", taxIncl: "(TTC)",
    modeLabel: "Affichage du prix", modeNone: "Sans prix", modeIncl: "Avec prix TTC", modeExcl: "Avec prix HT", modeLegacy: "Prix demandé (ancien lien)",
    missing: (mode, titles) => `Pour partager avec prix ${mode === "incl" ? "TTC" : "HT"}, renseignez ce montant pour : ${titles}. Ou choisissez « Sans prix ».`,
    modeHelp: "Le mode s'applique à tout le lien. En « Sans prix », aucun montant n'est transmis au client.",
  },
  en: {
    excl: "Price excl. tax", incl: "Price incl. tax", currency: "Currency", why: "Why this work for this client?", save: "Save",
    gallery: (a, cur) => `Gallery price: ${Number(a).toLocaleString("en-GB")} ${cur || ""}`.trim(),
    asHt: "Use as excl. tax", asTtc: "Use as incl. tax", unspecified: "The document does not say whether tax is included: your call.",
    taxExcl: "(excl. tax)", taxIncl: "(incl. tax)",
    modeLabel: "Price display", modeNone: "No price", modeIncl: "With price incl. tax", modeExcl: "With price excl. tax", modeLegacy: "Asking price (older link)",
    missing: (mode, titles) => `To share with a price ${mode === "incl" ? "incl. tax" : "excl. tax"}, fill in that amount for: ${titles}. Or choose “No price”.`,
    modeHelp: "The mode applies to the whole link. With “No price”, no amount is sent to the client.",
  },
  de: {
    excl: "Preis netto", incl: "Preis brutto", currency: "Währung", why: "Warum dieses Werk für diesen Kunden?", save: "Speichern",
    gallery: (a, cur) => `Galeriepreis: ${Number(a).toLocaleString("de-DE")} ${cur || ""}`.trim(),
    asHt: "Als netto übernehmen", asTtc: "Als brutto übernehmen", unspecified: "Das Dokument nennt nicht, ob Steuer enthalten ist: Ihre Entscheidung.",
    taxExcl: "(netto)", taxIncl: "(brutto)",
    modeLabel: "Preisanzeige", modeNone: "Ohne Preis", modeIncl: "Mit Preis brutto", modeExcl: "Mit Preis netto", modeLegacy: "Angebotspreis (älterer Link)",
    missing: (mode, titles) => `Zum Teilen mit Preis ${mode === "incl" ? "brutto" : "netto"} diesen Betrag ergänzen für: ${titles}. Oder „Ohne Preis“ wählen.`,
    modeHelp: "Der Modus gilt für den ganzen Link. Bei „Ohne Preis“ wird dem Kunden kein Betrag übermittelt.",
  },
};
const CURRENCIES = ["EUR", "USD", "GBP", "CHF"];

const card = { background: "#fff", border: "1px solid #e5e3df", borderRadius: 4, padding: 16 };
const input = { padding: "8px 10px", border: "1px solid #e5e3df", borderRadius: 2, fontSize: 13, fontFamily: "system-ui, sans-serif", outline: "none", width: "100%", boxSizing: "border-box" };
const btn = { padding: "8px 14px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 2, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "system-ui, sans-serif" };
const btnGhost = { ...btn, background: "#fff", color: "#1a1a1a", border: "1px solid #1a1a1a" };
const eyebrow = { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8b7355", fontWeight: 700, fontFamily: "system-ui, sans-serif" };

// Code d'état du lien (l'étiquette affichée est traduite à part)
const linkStatus = (l) => {
  if (l.revoked_at) return "revoked";
  if (new Date(l.expires_at).getTime() <= Date.now()) return "expired";
  if (l.submitted_at) return "submitted";
  return "active";
};
const LINK_STATUS_FR = { revoked: "Révoqué", expired: "Expiré", submitted: "Réponses reçues", active: "Actif" };

// Texte avec un passage en gras balisé <b>…</b> (l'ordre des mots varie selon la langue)
const Rich = ({ text }) => text.split(/(<b>.*?<\/b>)/g).map((p, i) => (p.startsWith("<b>") ? <strong key={i}>{p.slice(3, -4)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>));

function ItemRow({ artwork, meta, offer, onSave }) {
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const t = PRICE_COPY[lang] || PRICE_COPY.fr;
  const [excl, setExcl] = useState(meta?.price_excl ?? "");
  const [incl, setIncl] = useState(meta?.price_incl ?? "");
  const [cur, setCur] = useState(meta?.currency || "EUR");
  const [why, setWhy] = useState(meta?.rationale || "");
  const dirty = String(excl) !== String(meta?.price_excl ?? "") || String(incl) !== String(meta?.price_incl ?? "")
    || cur !== (meta?.currency || "EUR") || why !== (meta?.rationale || "");
  const hint = galleryPriceHint(offer);
  const num = (v) => (v === "" || v == null ? null : Number(v));
  return (
    <div style={{ ...card, display: "grid", gridTemplateColumns: "88px 1fr", gap: 14 }}>
      {artwork.image_url ? (
        <img src={artwork.image_url} alt="" style={{ width: 88, height: 88, objectFit: "cover", borderRadius: 2 }} />
      ) : (
        <div style={{ width: 88, height: 88, background: "#f3f0ea", borderRadius: 2 }} />
      )}
      <div style={{ display: "grid", gap: 8, fontFamily: "system-ui, sans-serif" }}>
        <div><strong>{artwork.title}</strong> <span style={{ color: "#6b6b6b" }}>· {artwork.artist}{artwork.year ? `, ${artwork.year}` : ""}</span></div>
        {hint && (
          <div style={{ fontSize: 12, color: "#6b6b6b" }}>
            {t.gallery(hint.amount, hint.currency)} {hint.tax === "excl" ? t.taxExcl : hint.tax === "incl" ? t.taxIncl : ""}
            {" "}
            {hint.tax !== "incl" && <button type="button" style={{ background: "none", border: "none", textDecoration: "underline", cursor: "pointer", fontSize: 12 }}
              onClick={() => { setExcl(hint.amount); if (hint.currency) setCur(hint.currency); }}>{t.asHt}</button>}
            {hint.tax !== "excl" && <button type="button" style={{ background: "none", border: "none", textDecoration: "underline", cursor: "pointer", fontSize: 12 }}
              onClick={() => { setIncl(hint.amount); if (hint.currency) setCur(hint.currency); }}>{t.asTtc}</button>}
            {!hint.tax && <div>{t.unspecified}</div>}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "130px 130px 90px", gap: 8 }}>
          <input style={input} type="number" min="0" placeholder={t.excl} value={excl} onChange={(e) => setExcl(e.target.value)} />
          <input style={input} type="number" min="0" placeholder={t.incl} value={incl} onChange={(e) => setIncl(e.target.value)} />
          <select style={input} value={cur} onChange={(e) => setCur(e.target.value)} aria-label={t.currency}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <input style={input} placeholder={t.why} value={why} onChange={(e) => setWhy(e.target.value)} />
        {dirty && (
          <div>
            <button style={btn} onClick={() => onSave(artwork.id, { price_excl: num(excl), price_incl: num(incl), currency: cur, rationale: why })}>{t.save}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AcquisitionProposals() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({ title: "", client: "" });
  const [artworks, setArtworks] = useState([]);
  const [metas, setMetas] = useState({});
  const [links, setLinks] = useState([]);
  const [offers, setOffers] = useState({});
  const [priceMode, setPriceMode] = useState("none");
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const t = PRICE_COPY[lang] || PRICE_COPY.fr;
  const { T, locale } = useFT();
  const [reactions, setReactions] = useState({});
  const [msg, setMsg] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const loadList = useCallback(async () => {
    if (!user?.id) return;
    const [{ data: cols }, { data: ents }] = await Promise.all([
      supabase.from("collections").select("id,title,description,created_at").eq("client_id", user.id).eq("collection_type", "proposal").order("created_at", { ascending: false }),
      supabase.from("advisor_client_entities").select("id,display_name").eq("primary_advisor_id", user.id).neq("status", "archived"),
    ]);
    setProposals(cols || []);
    setClients(ents || []);
  }, [user?.id]);

  const loadDetail = useCallback(async (id) => {
    const [{ data: arts }, { data: items }, { data: lks }, { data: offs }] = await Promise.all([
      supabase.from("artworks").select("id,title,artist,year,image_url,medium,dimensions").eq("collection_id", id),
      supabase.from("proposal_items").select("*").eq("collection_id", id),
      supabase.from("proposal_links").select("*").eq("collection_id", id).order("created_at", { ascending: false }),
      supabase.from("proposal_offers").select("artwork_id,price_status,price_amount,price_currency,price_tax").eq("collection_id", id),
    ]);
    setOffers(Object.fromEntries((offs || []).filter((o) => o.artwork_id).map((o) => [o.artwork_id, o])));
    setArtworks(arts || []);
    setMetas(Object.fromEntries((items || []).map((m) => [m.artwork_id, m])));
    setLinks(lks || []);
    const ids = (lks || []).map((l) => l.id);
    if (ids.length) {
      const { data: rx } = await supabase.from("proposal_reactions").select("link_id,artwork_id,reaction,comment").in("link_id", ids);
      const by = {};
      (rx || []).forEach((r) => { (by[r.link_id] ||= []).push(r); });
      setReactions(by);
    } else setReactions({});
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (openId) loadDetail(openId); }, [openId, loadDetail]);

  const create = async () => {
    const title = form.title.trim();
    if (!title) { setMsg(T("Donnez un nom à la proposition.")); return; }
    const { data, error } = await supabase.from("collections")
      .insert({ title, client_id: user.id, collection_type: "proposal", description: form.client.trim() || null })
      .select("id").single();
    if (error) { setMsg(error.message); return; }
    setForm({ title: "", client: "" });
    setMsg("");
    clearProposalCollectionCache();
    await loadList();
    setOpenId(data.id);
  };

  const saveItem = async (artworkId, fields) => {
    const validated = fields.price_excl != null || fields.price_incl != null;
    const { error } = await supabase.from("proposal_items").upsert(
      { collection_id: openId, artwork_id: artworkId, ...fields, price_validated_at: validated ? new Date().toISOString() : null, updated_at: new Date().toISOString() },
      { onConflict: "artwork_id" }
    );
    if (error) setMsg(error.message); else { setMsg(""); loadDetail(openId); }
  };

  const createLink = async () => {
    const open = proposals.find((p) => p.id === openId);
    // Partage avec prix : le montant du mode choisi doit être validé pour chaque œuvre, jamais calculé à partir de l'autre.
    const rows = artworks.map((a) => ({ id: a.id, title: a.title, price_excl: metas[a.id]?.price_excl ?? null, price_incl: metas[a.id]?.price_incl ?? null }));
    const missing = missingPrices(rows, priceMode);
    if (missing.length) { setMsg(t.missing(priceMode, missing.map((m) => m.title).join(", "))); return; }
    const { error } = await supabase.from("proposal_links").insert({
      collection_id: openId, advisor_id: user.id, token: newProposalToken(), client_label: form.client.trim() || open?.description || null,
      price_mode: priceMode,
    });
    if (error) setMsg(error.message); else { setMsg(""); loadDetail(openId); }
  };

  const downloadPdf = async () => {
    const open = proposals.find((p) => p.id === openId);
    const rows = artworks.map((a) => ({ ...a, ...(metas[a.id] || {}), id: a.id }));
    const missing = missingPrices(rows, priceMode);
    if (missing.length) { setMsg(t.missing(priceMode, missing.map((m) => m.title).join(", "))); return; }
    setPdfBusy(true);
    try {
      const items = await Promise.all(rows.map(async (r) => ({ ...r, image: await imageToDataUrl(r.image_url) })));
      await downloadProposalClientPdf({
        title: open?.title, clientLabel: open?.description || form.client.trim() || "", advisorName: "",
        mode: priceMode, lang: ["fr", "en", "de"].includes(lang) ? lang : "fr", items,
      });
      setMsg("");
    } catch (e) {
      setMsg(e?.message || String(e));
    } finally {
      setPdfBusy(false);
    }
  };

  const revoke = async (id) => {
    await supabase.from("proposal_links").update({ revoked_at: new Date().toISOString() }).eq("id", id);
    loadDetail(openId);
  };

  const copy = async (token) => {
    const url = `${window.location.origin}/proposition/${token}`;
    try { await navigator.clipboard.writeText(url); setMsg(T("Lien copié.")); } catch { setMsg(url); }
  };

  const open = proposals.find((p) => p.id === openId);
  const artTitle = (id) => artworks.find((a) => a.id === id)?.title || T("Œuvre");

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 20px 80px", fontFamily: "Georgia, serif" }}>
      <div style={eyebrow}>{T("Espace Art Advisor")}</div>
      <h1 style={{ fontSize: 30, margin: "4px 0 6px" }}>{T("Propositions d'acquisition")}</h1>
      <p style={{ color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 14, maxWidth: 680 }}>
        <Rich text={T("Composez une sélection d'œuvres <b>non détenues</b> pour un client. Elle n'entre jamais dans son inventaire. Vous partagez un lien valable 14 jours ; il compare 2 ou 3 œuvres et vous renvoie ses réactions.")} />
      </p>
      {msg && <p style={{ fontFamily: "system-ui, sans-serif", fontSize: 13, color: "#8f3a44" }}>{msg}</p>}

      <div style={{ ...card, margin: "20px 0", display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }}>
        <div><div style={eyebrow}>{T("Nom de la proposition")}</div><input style={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={T("Ex. : Sélection printemps")} /></div>
        <div>
          <div style={eyebrow}>{T("Client")}</div>
          <input style={input} list="adv-clients" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder={T("Nom affiché au client")} />
          <datalist id="adv-clients">{clients.map((c) => <option key={c.id} value={c.display_name} />)}</datalist>
        </div>
        <button style={btn} onClick={create}>{T("Nouvelle proposition")}</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 20, alignItems: "start" }}>
        <div style={{ display: "grid", gap: 8 }}>
          {proposals.length === 0 && <div style={{ color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>{T("Aucune proposition pour le moment.")}</div>}
          {proposals.map((p) => (
            <button key={p.id} onClick={() => setOpenId(p.id)}
              style={{ ...card, textAlign: "left", cursor: "pointer", borderColor: p.id === openId ? "#1a1a1a" : "#e5e3df" }}>
              <div style={{ fontWeight: 600 }}>{p.title}</div>
              <div style={{ color: "#9ca3af", fontSize: 12, fontFamily: "system-ui, sans-serif" }}>{new Date(p.created_at).toLocaleDateString(locale)}</div>
            </button>
          ))}
        </div>

        {open ? (
          <div style={{ display: "grid", gap: 14 }}>
            <ImportOffersPdf collectionId={open.id} onDone={() => loadDetail(open.id)} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <h2 style={{ fontSize: 22, margin: 0 }}>{open.title}</h2>
              <button style={btnGhost} onClick={() => navigate("/add", { state: { preselectedCollectionId: open.id } })}>{T("+ Ajouter une œuvre")}</button>
            </div>
            {artworks.length === 0 && <div style={{ ...card, color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>{T("Aucune œuvre. Ajoutez-en avec « Ajouter une œuvre » : elles sont créées comme n'importe quelle fiche, dans cette proposition.")}</div>}
            {artworks.map((a) => <ItemRow key={a.id + (metas[a.id]?.updated_at || "")} artwork={a} meta={metas[a.id]} offer={offers[a.id]} onSave={saveItem} />)}

            <div style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={eyebrow}>{T("Partage au client")}</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select style={{ ...input, width: "auto" }} value={priceMode} onChange={(e) => setPriceMode(e.target.value)} aria-label={t.modeLabel}>
                    <option value="none">{t.modeNone}</option>
                    <option value="incl">{t.modeIncl}</option>
                    <option value="excl">{t.modeExcl}</option>
                  </select>
                  <button style={btn} onClick={createLink} disabled={artworks.length === 0}>{T("Créer un lien (14 jours)")}</button>
                  <button style={btnGhost} onClick={downloadPdf} disabled={artworks.length === 0 || pdfBusy}>{pdfBusy ? T("Préparation du PDF…") : T("PDF client")}</button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#8a8378", fontFamily: "system-ui, sans-serif", marginBottom: 6 }}>{t.modeHelp} {T("Le PDF client suit le même mode de prix.")}</div>
              {links.length === 0 && <div style={{ color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>{T("Aucun lien créé.")}</div>}
              {links.map((l) => {
                const rx = reactions[l.id] || [];
                return (
                  <div key={l.id} style={{ borderTop: "1px solid #f0eee9", padding: "10px 0", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <span><strong>{T(LINK_STATUS_FR[linkStatus(l)])}</strong> · {({ none: t.modeNone, incl: t.modeIncl, excl: t.modeExcl, legacy: t.modeLegacy })[l.price_mode] || t.modeNone} · {T("expire le {date}", { date: new Date(l.expires_at).toLocaleDateString(locale) })} · {T("{n} vue(s)", { n: l.view_count })}</span>
                      <span style={{ display: "flex", gap: 10 }}>
                        {linkStatus(l) === "active" || linkStatus(l) === "submitted" ? <button onClick={() => copy(l.token)} style={{ background: "none", border: "none", textDecoration: "underline", cursor: "pointer" }}>{T("Copier le lien")}</button> : null}
                        {!l.revoked_at && <button onClick={() => revoke(l.id)} style={{ background: "none", border: "none", color: "#8f3a44", cursor: "pointer" }}>{T("Révoquer")}</button>}
                      </span>
                    </div>
                    {rx.length > 0 && (
                      <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                        {rx.map((r) => (
                          <li key={r.artwork_id}><strong>{artTitle(r.artwork_id)}</strong> : {T(REACTION_LABELS[r.reaction] || "")}{r.comment ? ` — « ${r.comment} »` : ""}</li>
                        ))}
                      </ul>
                    )}
                    {l.submitted_at && <div style={{ color: "#2c5f2d", marginTop: 6 }}>{T("Réponses envoyées le {date} (mail récapitulatif envoyé).", { date: new Date(l.submitted_at).toLocaleDateString(locale) })}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 14 }}>{T("Sélectionnez ou créez une proposition.")}</div>
        )}
      </div>
    </div>
  );
}
